import React, { useEffect, useCallback, useState, useRef } from "react";
import { FaTrash, FaPlus } from "react-icons/fa";
import CarouselMediaConfig from "./CarouselMediaConfig";
import { captionLengthTone, MAX_ADMIN_HASHTAGS } from "../../../helpers/captionBudget";

const ParticipationAdditionalData = React.memo(
  ({
    // States for participation post
    ppCaption1,
    setPpCaption1,
    ppCaption2,
    setPpCaption2,

    // Defensive defaults to avoid undefined errors
    ppHashtags = [],
    setPpHashtags = () => {},
    newHashtag = "",
    setNewHashtag = () => {},
    frameImageUrl = null,
    setFrameImageUrl = () => {},
    participationCarouselImages = [],
    setParticipationCarouselImages = () => {},

    // Editable toggles (also default safely)
    isPPHashtagEditable = true,
    setIsPPHashtagEditable = () => {},
    isPPCaptionEditable = true,
    setIsPPCaptionEditable = () => {},
    arePPSponsorsTagEditable = true,
    setArePPSponsorsTagEditable = () => {},
    isDefaultChecked = false,
    setIsDefaultChecked = () => {},
    isEditMode = false,

    // File upload handler
    handleFileUpload = async () => null,

    // OPTIONAL: parent can pass this to disable Next while uploading
    onUploadingChange,
    // Template / Post Frame
    templateData = null,
    onTemplateDataChange = null,
    errors = {},
    setErrors = () => {},
    // Carousel Props
    isParticipationCarousel = false,
    setIsParticipationCarousel = () => {},
    participationCarouselConfig = { adminMediaCount: 0, userMediaCount: 0, mediaItems: [] },
    setParticipationCarouselConfig = () => {},
  }) => {
    // State to manage previews
    const [frameImagePreview, setFrameImagePreview] = useState(null);
    const [carouselImagePreviews, setCarouselImagePreviews] = useState([]);

    // Track in-flight uploads (so parent can disable navigation if desired)
    const [uploadCount, setUploadCount] = useState(0);
    const inc = () => setUploadCount((c) => c + 1);
    const dec = () => setUploadCount((c) => Math.max(0, c - 1));

    // Notify parent when uploading state changes
    useEffect(() => {
      if (typeof onUploadingChange === "function") {
        onUploadingChange(uploadCount > 0);
      }
    }, [uploadCount, onUploadingChange]);

    // Keep a list of blob URLs to revoke on unmount
    const blobUrlsRef = useRef([]);
    const makePreview = (file) => {
      const url = URL.createObjectURL(file);
      blobUrlsRef.current.push(url);
      return url;
    };
    useEffect(() => {
      return () => {
        blobUrlsRef.current.forEach((u) => {
          try {
            URL.revokeObjectURL(u);
          } catch {}
        });
        blobUrlsRef.current = [];
      };
    }, []);

    // -------- Hydration / Rehydration --------
    // Initialize non-image fields in edit mode (same as your old logic)
    useEffect(() => {
      if (isEditMode) {
        setCarouselImagePreviews(
          Array.isArray(participationCarouselImages)
            ? participationCarouselImages.map((img) => (img && img.url) || null)
            : []
        );
      }
    }, [isEditMode, participationCarouselImages]);

    // Always keep the single frame preview in sync with persisted URL,
    // even in create mode or after navigating away/back.
    useEffect(() => {
      if (
        frameImageUrl &&
        (!frameImagePreview || !frameImagePreview.startsWith("blob:"))
      ) {
        setFrameImagePreview(frameImageUrl);
      }
    }, [frameImageUrl, frameImagePreview]);

    // Keep carousel previews in sync with persisted URLs when returning to this step.
    // Only fill from URL if there's no local blob preview for that slot.
    useEffect(() => {
      const imgs = Array.isArray(participationCarouselImages)
        ? participationCarouselImages
        : [];
      setCarouselImagePreviews((prev) => {
        const next = [...prev];
        for (let i = 0; i < imgs.length; i++) {
          const hasBlob = next[i]?.startsWith?.("blob:");
          const persistedUrl = imgs[i]?.url || null;
          if (!hasBlob && persistedUrl) {
            next[i] = persistedUrl;
          } else if (next[i] === undefined) {
            next[i] = null; // keep indexes aligned
          }
        }
        // If parent removed images and prev had extra entries, trim
        return next.slice(0, imgs.length);
      });
    }, [participationCarouselImages]);

    // -------- Hashtags (normalized) --------
    const normalizeTag = (s = "") => s.trim().replace(/^#+/, ""); // remove any leading # characters
    const [hashtagNotice, setHashtagNotice] = useState("");
    const atHashtagCap =
      (Array.isArray(ppHashtags) ? ppHashtags.length : 0) >= MAX_ADMIN_HASHTAGS;

    // Admin hashtags are capped at MAX_ADMIN_HASHTAGS (5) — Instagram now
    // hard-limits a post to far fewer than before, and the user app adds up
    // to 5 of its own on top, so the admin block must stay <= 5. This single
    // helper dedups AND enforces the cap; every add-path routes through it so
    // none can sneak past the limit (Trishul flagged 2026-06-04 the Add button
    // let a 6th tag through). Returns the count of tags dropped for being over
    // the cap so callers can surface a notice.
    const addTagsCapped = useCallback(
      (parts) => {
        const tags = Array.isArray(ppHashtags) ? ppHashtags : [];
        const existingLower = tags.map((t) => String(t ?? "").trim().toLowerCase());
        const fresh = parts.filter((t) => !existingLower.includes(t.toLowerCase()));
        const room = Math.max(0, MAX_ADMIN_HASHTAGS - tags.length);
        const toAdd = fresh.slice(0, room);
        const dropped = fresh.length - toAdd.length;
        if (toAdd.length > 0) setPpHashtags([...tags, ...toAdd]);
        if (dropped > 0) {
          setHashtagNotice(
            `Only ${MAX_ADMIN_HASHTAGS} event hashtags are allowed — ${dropped} not added.`
          );
        } else {
          setHashtagNotice("");
        }
        return dropped;
      },
      [ppHashtags, setPpHashtags]
    );

    const addParticipationHashtag = useCallback(() => {
      const parts = (newHashtag ?? "").split(",").map(normalizeTag).filter(Boolean);
      if (parts.length === 0) return;
      addTagsCapped(parts);
      setNewHashtag("");
    }, [newHashtag, addTagsCapped, setNewHashtag]);

    const handleParticipationHashtagPaste = useCallback((e) => {
      const pasted = e.clipboardData.getData("text");
      const parts = pasted.split(",").map(normalizeTag).filter(Boolean);
      if (parts.length <= 1) return;
      e.preventDefault();
      addTagsCapped(parts);
      setNewHashtag("");
    }, [addTagsCapped, setNewHashtag]);

    const removeParticipationHashtag = useCallback(
      (index) => {
        const tags = Array.isArray(ppHashtags) ? ppHashtags : [];
        setPpHashtags(tags.filter((_, i) => i !== index));
        setHashtagNotice("");
      },
      [ppHashtags, setPpHashtags]
    );

    const handleTextareaResize = (e) => {
      e.target.style.height = "auto";
      e.target.style.height = `${e.target.scrollHeight}px`;
    };

    // -------- Carousel Images --------
    const addCarouselImage = useCallback(() => {
      setParticipationCarouselImages([
        ...(Array.isArray(participationCarouselImages)
          ? participationCarouselImages
          : []),
        { url: null },
      ]);
      setCarouselImagePreviews((prev) => [...prev, null]);
    }, [participationCarouselImages, setParticipationCarouselImages]);

    const removeCarouselImage = useCallback(
      (index) => {
        setParticipationCarouselImages(
          (Array.isArray(participationCarouselImages)
            ? participationCarouselImages
            : []
          ).filter((_, i) => i !== index)
        );
        setCarouselImagePreviews((prev) => prev.filter((_, i) => i !== index));
      },
      [participationCarouselImages, setParticipationCarouselImages]
    );

    const handleCarouselImageUpload = useCallback(
      async (file, index) => {
        if (!file) return;
        const previewUrl = makePreview(file);
        setCarouselImagePreviews((prev) => {
          const updated = [...prev];
          updated[index] = previewUrl;
          return updated;
        });

        try {
          inc();
          const uploadedUrl = await handleFileUpload(file);
          setParticipationCarouselImages((prevImages) => {
            const updated = [...(Array.isArray(prevImages) ? prevImages : [])];
            updated[index] = { url: uploadedUrl || null };
            return updated;
          });
        } catch (e) {
          console.error("Carousel image upload failed:", e);
          setParticipationCarouselImages((prevImages) => {
            const updated = [...(Array.isArray(prevImages) ? prevImages : [])];
            updated[index] = { url: null };
            return updated;
          });
        } finally {
          dec();
        }
      },
      [setParticipationCarouselImages, handleFileUpload]
    );

    // -------- Post Frame / Frame Image --------
    const handleFrameImageUpload = useCallback(
      async (file) => {
        if (!file) return;
        const previewUrl = makePreview(file);
        setFrameImagePreview(previewUrl);

        try {
          inc();
          const uploadedUrl = await handleFileUpload(file);
          setFrameImageUrl(uploadedUrl || null);
          // Also propagate as templateData to parent form
          if (typeof onTemplateDataChange === "function" && uploadedUrl) {
            onTemplateDataChange(uploadedUrl);
          }
        } catch (e) {
          console.error("Post Frame upload failed:", e);
          setFrameImageUrl(null);
        } finally {
          dec();
        }
      },
      [setFrameImageUrl, handleFileUpload, onTemplateDataChange]
    );

    return (
      <div className="p-4 border rounded-md bg-blue-50 mb-6">
        {/* Optional uploading banner */}
        {uploadCount > 0 && (
          <div className="mb-4 p-3 rounded bg-yellow-100 text-yellow-800 text-sm">
            Uploading files… Please wait. You can’t proceed until uploads
            finish.
          </div>
        )}

        <h2 className="text-lg font-semibold mb-2">
          Participation Editable Checkbox
        </h2>

        {/* Editable Toggles */}
        <section className="grid grid-cols-1 lg:grid-cols-3 gap-10 bg-gray-50 p-4 border rounded-md mb-5">
          <div className="flex items-center gap-4">
            <label className="text-base lg:text-lg font-medium">
              Event Hashtags (Extendable)
            </label>
            <input
              type="checkbox"
              checked={!!isPPHashtagEditable}
              onChange={() => setIsPPHashtagEditable(!isPPHashtagEditable)}
              className="h-5 w-5"
            />
          </div>
          <div className="flex items-center gap-4">
            <label className="text-base lg:text-lg font-medium">
              Event Caption (Extendable)
            </label>
            <input
              type="checkbox"
              checked={!!isPPCaptionEditable}
              onChange={() => setIsPPCaptionEditable(!isPPCaptionEditable)}
              className="h-5 w-5"
            />
          </div>
          <div className="flex items-center gap-4">
            <label className="text-base lg:text-lg font-medium">
              Sponser Tags Extendable
            </label>
            <input
              type="checkbox"
              checked={!!arePPSponsorsTagEditable}
              onChange={() =>
                setArePPSponsorsTagEditable(!arePPSponsorsTagEditable)
              }
              className="h-5 w-5"
            />
          </div>
        </section>

        {/* Participation Carousel Toggle */}
        <section className="bg-purple-50 p-4 border border-purple-200 rounded-md mb-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="text-xl">🎠</span>
              <div>
                <label className="text-lg font-semibold text-purple-800">
                  Enable Participation Carousel
                </label>
                <p className="text-sm text-purple-600">
                  Create an Instagram carousel for participant posts
                </p>
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={!!isParticipationCarousel}
                onChange={() => setIsParticipationCarousel(!isParticipationCarousel)}
                className="sr-only peer"
              />
              <div className="w-14 h-7 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[4px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-purple-600"></div>
            </label>
          </div>
        </section>

        {/* Participation Captions */}
        <div className="mb-4">
          <label className={`text-base lg:text-lg font-medium mb-2 flex items-center justify-between ${errors.ppCaption1 ? 'text-red-500' : ''}`}>
            <span>
              Participation post caption {errors.ppCaption1 && <span className="text-red-500 text-sm ml-2 font-normal">*Required</span>}
            </span>
            {(() => {
              const len = (ppCaption1 || "").length;
              // Item 7 (2026-06-04): soft-warn near the 1750 hard cap.
              const tone = captionLengthTone(len, 1750);
              const color = tone === "red" ? "text-red-600 bg-red-50" : tone === "yellow" ? "text-yellow-700 bg-yellow-50" : "text-gray-500 bg-gray-100";
              return (
                <span className={`text-xs font-normal px-2 py-0.5 rounded ${color}`}>
                  {len}/1750 chars · leaves room for hashtags + NNexus code + participant text
                </span>
              );
            })()}
          </label>
          <textarea
            value={ppCaption1}
            onChange={(e) => {
              setPpCaption1(e.target.value);
              if (errors.ppCaption1) setErrors({ ...errors, ppCaption1: false });
            }}
            onInput={handleTextareaResize}
            className={`w-full lg:min-h-40 border rounded-md px-4 py-2 ${errors.ppCaption1 ? 'border-red-500 error-field' : ''}`}
            placeholder="Enter participation post caption"
          />
        </div>


        {/* Participation Hashtags */}
        <div className="mb-4">
          <label className="text-lg font-medium flex items-center justify-between">
            <span>Hashtags</span>
            <span
              className={`text-xs font-normal px-2 py-0.5 rounded ${
                atHashtagCap ? "text-red-600 bg-red-50" : "text-gray-500 bg-gray-100"
              }`}
            >
              {(Array.isArray(ppHashtags) ? ppHashtags.length : 0)}/{MAX_ADMIN_HASHTAGS} event hashtags
            </span>
          </label>
          <div className="flex items-center gap-4 mb-1 mt-2">
            <input
              type="text"
              value={newHashtag || ""}
              disabled={atHashtagCap}
              onChange={(e) => {
                const val = e.target.value;
                if (val.endsWith(",")) {
                  const tag = normalizeTag(val.slice(0, -1));
                  if (tag) {
                    addTagsCapped([tag]);
                    setNewHashtag("");
                  } else {
                    setNewHashtag(val);
                  }
                } else {
                  setNewHashtag(val);
                }
              }}
              onKeyDown={(e) => {
                if ((e.key === "Tab" || e.key === "Enter") && normalizeTag(newHashtag || "")) {
                  e.preventDefault();
                  addParticipationHashtag();
                }
              }}
              onPaste={handleParticipationHashtagPaste}
              className="flex-grow border rounded-md px-4 py-2 disabled:bg-gray-100 disabled:cursor-not-allowed"
              placeholder={
                atHashtagCap
                  ? `Limit reached — remove a hashtag to add another`
                  : "Add hashtag(s) — separate with comma"
              }
            />
            <button
              type="button"
              onClick={addParticipationHashtag}
              disabled={atHashtagCap}
              className="bg-green-500 text-white px-4 py-2 rounded-md flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FaPlus /> Add
            </button>
          </div>
          {hashtagNotice && (
            <p className="text-xs text-red-600 mb-3">{hashtagNotice}</p>
          )}
          <div className="flex flex-wrap gap-2">
            {(ppHashtags || []).map((tag, index) => (
              <span
                key={`${tag}-${index}`}
                className="bg-gray-200 px-3 py-1 rounded-full flex items-center gap-2"
              >
                #{tag}
                <button
                  type="button"
                  onClick={() => removeParticipationHashtag(index)}
                  className="text-red-600"
                >
                  <FaTrash />
                </button>
              </span>
            ))}
          </div>
        </div>

        {/* Standard Media Section — always visible */}
        {/* Post Frame Upload */}
        <div className="mb-4">
          <label className="block text-lg font-medium">Post Frame</label>
          <input
            type="file"
            accept="image/*"
            onChange={(e) => handleFrameImageUpload(e.target.files?.[0])}
            className="border px-4 py-2 rounded-md"
          />
          {(frameImagePreview || frameImageUrl || templateData) && (
            <img
              src={frameImagePreview || frameImageUrl || templateData}
              alt="Post Frame Preview"
              className="mt-4 w-60 h-60 object-cover rounded-md"
            />
          )}
        </div>

        {/* Participation Carousel Images — Table Layout */}
        <div className="mb-4">
          <label className="block text-lg font-medium mb-2">Carousel Images</label>
          <div className="border rounded-md overflow-hidden">
            <table className="w-full border-collapse text-sm">
              <thead className="bg-gray-100">
                <tr>
                  <th className="text-left px-3 py-2 border-b font-semibold text-gray-600 w-10">#</th>
                  <th className="text-left px-3 py-2 border-b font-semibold text-gray-600 w-20">Preview</th>
                  <th className="text-left px-3 py-2 border-b font-semibold text-gray-600">Select File</th>
                  <th className="px-3 py-2 border-b font-semibold text-gray-600 w-14 text-center">Del</th>
                </tr>
              </thead>
              <tbody>
                {(participationCarouselImages || []).length === 0 && (
                  <tr className="bg-white">
                    <td className="px-3 py-3 text-gray-400 align-middle">1</td>
                    <td className="px-3 py-3 align-middle">
                      <div className="w-14 h-14 bg-gray-100 rounded flex items-center justify-center text-gray-400">
                        <FaPlus />
                      </div>
                    </td>
                    <td className="px-3 py-3 align-middle">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleCarouselImageUpload(e.target.files?.[0], 0)}
                        className="text-sm"
                      />
                    </td>
                    <td className="px-3 py-3 align-middle text-center text-gray-300">—</td>
                  </tr>
                )}
                {(participationCarouselImages || []).map((image, index) => (
                  <tr key={index} className="border-t bg-white hover:bg-gray-50">
                    <td className="px-3 py-3 text-gray-500 align-middle">{index + 1}</td>
                    <td className="px-3 py-3 align-middle">
                      {(carouselImagePreviews[index] || image?.url) ? (
                        <img
                          src={carouselImagePreviews[index] || image?.url}
                          alt={`carousel-${index + 1}`}
                          className="w-14 h-14 object-cover rounded"
                        />
                      ) : (
                        <div className="w-14 h-14 bg-gray-100 rounded flex items-center justify-center text-gray-400">
                          <FaPlus />
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-3 align-middle">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleCarouselImageUpload(e.target.files?.[0], index)}
                        className="text-sm"
                      />
                    </td>
                    <td className="px-3 py-3 align-middle text-center">
                      <button
                        type="button"
                        onClick={() => removeCarouselImage(index)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <FaTrash />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="p-3 bg-gray-50 border-t">
              <button
                type="button"
                onClick={addCarouselImage}
                className="px-4 py-2 bg-green-500 text-white rounded-md flex items-center gap-2 text-sm hover:bg-green-600 transition"
              >
                <FaPlus /> Add Image
              </button>
            </div>
          </div>
        </div>

        {/* Carousel Configuration — shown when toggle is ON */}
        {isParticipationCarousel && (
          <div className="mb-4">
            <CarouselMediaConfig
              config={participationCarouselConfig}
              onConfigChange={setParticipationCarouselConfig}
              handleFileUpload={handleFileUpload}
              configKey="participationCarousel"
              isEditMode={isEditMode}
              onUploadingChange={onUploadingChange}
              frameImageUrl={frameImageUrl}
            />
          </div>
        )}
      </div>
    );
  }
);

export default ParticipationAdditionalData;
